// An insurer scheme's benefit rules in Settings > Payers (docs/plans/phase2-private-insurance.md
// §2.3, §6 item 9): limits, excess and which services need the insurer's approval. Rules are
// never edited: a change is a new rule, and the old one is stopped with a reason.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SERVICE_ITEM_CATEGORIES } from '@wodi/domain';
import type { BenefitRuleView, SchemeView } from '@wodi/shared';
import { Button, LoadingBlock, MoneyField, Notice, Select, Tag } from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  benefitRulesQuery,
  createBenefitRule,
  deactivateBenefitRule,
  insuranceKeys,
} from '../api/insurance.ts';
import { CATEGORY_LABELS } from '../finance/labels.ts';
import { useOnline } from '../hooks/use-online.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import styles from './insurance.module.css';

type Kind = BenefitRuleView['kind'];

const KIND_OPTIONS: { id: Kind; label: string }[] = [
  { id: 'limit', label: 'Limit: the insurer pays up to' },
  { id: 'excess', label: 'Excess: the patient pays first' },
  { id: 'preauth_required', label: 'Needs the insurer’s approval' },
];

const EVERY = 'every';

function AddRuleForm({ schemeId, onDone }: { schemeId: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<Kind>('limit');
  const [period, setPeriod] = useState<'per_visit' | 'per_year'>('per_year');
  const [category, setCategory] = useState<string>(EVERY);
  const [amount, setAmount] = useState<bigint | null>(null);
  const [tried, setTried] = useState(false);
  const needsAmount = kind !== 'preauth_required';
  const bad = amount === null ? needsAmount : needsAmount ? amount <= 0n : amount < 0n;
  const save = useMutation({
    mutationFn: () =>
      createBenefitRule(schemeId, {
        kind,
        period: kind === 'preauth_required' ? null : period,
        category: category === EVERY ? null : category,
        amount: { currency: 'KES', cents: (amount ?? 0n).toString() },
      }),
    onSuccess: async () => {
      toasts.show({ title: 'Benefit rule added' });
      await queryClient.invalidateQueries({ queryKey: insuranceKeys.rules(schemeId) });
      onDone();
    },
  });
  return (
    <form
      className={styles.form}
      aria-label="Add benefit rule"
      onSubmit={(e) => {
        e.preventDefault();
        setTried(true);
        if (!bad) save.mutate();
      }}
    >
      <div className={styles.fields}>
        <Select
          label="Rule"
          options={KIND_OPTIONS}
          value={kind}
          onChange={(v) => {
            setKind((v ?? 'limit') as Kind);
          }}
        />
        {kind === 'preauth_required' ? null : (
          <Select
            label="Period"
            options={[
              { id: 'per_visit', label: 'Per visit' },
              { id: 'per_year', label: 'Per benefit year' },
            ]}
            value={period}
            onChange={(v) => {
              setPeriod(v === 'per_visit' ? 'per_visit' : 'per_year');
            }}
          />
        )}
        <Select
          label="For"
          options={[
            { id: EVERY, label: 'Every service and item' },
            ...SERVICE_ITEM_CATEGORIES.map((c) => ({
              id: c,
              label: CATEGORY_LABELS[c],
            })),
          ]}
          value={category}
          onChange={(v) => {
            setCategory(String(v ?? EVERY));
          }}
        />
        <MoneyField
          label={kind === 'preauth_required' ? 'From a charge of' : 'Amount'}
          {...(kind === 'preauth_required'
            ? { description: 'Enter 0 when every such service needs approval.' }
            : {})}
          value={amount}
          onChange={(cents) => {
            setAmount(cents);
          }}
          {...(tried && bad ? { errorMessage: 'Enter an amount more than zero.' } : {})}
        />
      </div>
      {save.error === null ? null : (
        <Notice tone="error">
          {save.error instanceof ApiError
            ? save.error.message
            : "The rule wasn't added. Try again."}
        </Notice>
      )}
      <div className={styles.actions}>
        <Button type="submit" variant="primary" isPending={save.isPending} pendingLabel="Adding…">
          Add rule
        </Button>
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function SchemeRules({ scheme, canManage }: { scheme: SchemeView; canManage: boolean }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const rules = useQuery(benefitRulesQuery(scheme.id));
  const [adding, setAdding] = useState(false);
  const [stopping, setStopping] = useState<BenefitRuleView | null>(null);
  const stop = useMutation({
    mutationFn: (reason: string) =>
      deactivateBenefitRule(stopping?.id ?? '', {
        expectedVersion: stopping?.version ?? 1,
        reason,
      }),
    onSuccess: async () => {
      toasts.show({ title: 'Benefit rule stopped' });
      setStopping(null);
      await queryClient.invalidateQueries({ queryKey: insuranceKeys.rules(scheme.id) });
    },
  });
  return (
    <div className={styles.stack}>
      <div className={styles.actions}>
        <strong>{scheme.name}</strong>
        {canManage && !adding ? (
          <Button
            dense
            isDisabled={!online}
            onPress={() => {
              setAdding(true);
            }}
          >
            {`Add benefit rule to ${scheme.name}`}
          </Button>
        ) : null}
      </div>
      {rules.isPending ? <LoadingBlock label="Loading benefit rules" rows={1} /> : null}
      {rules.isError ? (
        <Notice tone="error">
          {rules.error instanceof ApiError ? rules.error.message : "The rules couldn't be loaded."}
        </Notice>
      ) : null}
      {rules.isSuccess && rules.data.items.length === 0 ? (
        <p className={styles.detail}>
          No limits, excess or approvals set: the split rule alone applies.
        </p>
      ) : null}
      {rules.isSuccess && rules.data.items.length > 0 ? (
        <ul className={styles.list} aria-label={`Benefit rules for ${scheme.name}`}>
          {rules.data.items.map((r) => (
            <li key={r.id} className={styles.row}>
              <span className={styles.grow}>{r.sentence}</span>
              {r.status === 'inactive' ? (
                <Tag state="neutral">Not in use</Tag>
              ) : canManage ? (
                <Button
                  dense
                  variant="quiet"
                  isDisabled={!online}
                  onPress={() => {
                    setStopping(r);
                  }}
                >
                  Stop using
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {adding ? (
        <AddRuleForm
          schemeId={scheme.id}
          onDone={() => {
            setAdding(false);
          }}
        />
      ) : null}
      <ReasonDialog
        title="Stop using this rule"
        isOpen={stopping !== null}
        onOpenChange={(open) => {
          if (!open) setStopping(null);
        }}
        confirmLabel="Stop using"
        pendingLabel="Saving…"
        isPending={stop.isPending}
        error={stop.error instanceof ApiError ? stop.error.message : null}
        onConfirm={(reason) => {
          stop.mutate(reason);
        }}
      >
        <p>Charges already made keep the amounts the rule gave them.</p>
      </ReasonDialog>
    </div>
  );
}

/** Every active scheme of an insurer, with its benefit rules. */
export function SchemeBenefits({
  schemes,
  canManage,
}: {
  schemes: readonly SchemeView[];
  canManage: boolean;
}) {
  const active = schemes.filter((s) => s.status === 'active');
  if (active.length === 0) return <p>Add a scheme first; benefits belong to a scheme.</p>;
  return (
    <div className={styles.stack}>
      {active.map((s) => (
        <SchemeRules key={s.id} scheme={s} canManage={canManage} />
      ))}
    </div>
  );
}
